import type { NextPage } from "next";
import Image, { StaticImageData } from "next/legacy/image";
import Page from "@/components/Page";

import directBanner from "../public/images/merchant-donation.jpg";
import Banner from "@/components/Banner";
import { icons, SocialType } from "@/components/Social";
import Thanks from "@/components/Thanks";

import hero from "../public/images/recipients-hero.jpg";
import { Heading2, Heading3 } from "@/components/Elements";

type SocialLink = {
  url: string;
  type: SocialType;
};

type Agency = {
  href: string;
  name: string;
  colour: string;
  imageURL: string | StaticImageData;
  social?: SocialLink[];
};

const agencies: Agency[] = [
  {
    href: "https://www.real.org.nz/",
    name: "Real",
    imageURL: "/logos/agencies/real.svg",
    colour: "bg-emerald-100",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/realNZyouth/",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/realnzyouth/",
      },
      {
        type: "youtube",
        url: "https://www.youtube.com/user/realNZyouth",
      },
    ],
  },
  {
    href: "https://www.kidsinneed.co.nz/",
    name: "Kids In Need",
    imageURL: "/logos/agencies/kids-in-need.png",
    colour: "bg-white",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/McKenzieCentreTrust",
      },
      {
        type: "instagram",
        url:
          "https://www.instagram.com/explore/locations/1027678793/mckenzie-centre-trust/",
      },
    ],
  },
  {
    href: "https://www.mckenziecentre.org.nz/",
    name: "McKenzie Centre",
    imageURL: "/logos/agencies/mckenzie-centre.svg",
    colour: "bg-white",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/McKenzieCentreTrust",
      },
      {
        type: "instagram",
        url:
          "https://www.instagram.com/explore/locations/1027678793/mckenzie-centre-trust/",
      },
    ],
  },
  {
    href: "https://www.grg.org.nz/",
    name: "Grandparents Raising Grandchildren",
    imageURL: "/logos/agencies/grandparents-raising-grandchildren.jpeg",
    colour: "bg-white",
    social: [
      {
        type: "facebook",
        url: "http://www.facebook.com/grg.org.nz",
      },
    ],
  },
  {
    href: "https://www.parentline.org.nz/",
    name: "Parent Line",
    imageURL: "/logos/agencies/parentline.png",
    colour: "bg-white",
  },
  {
    href: "https://www.rainbowplace.co.nz/",
    name: "Rainbow Place",
    imageURL: "/logos/agencies/rainbow-place.svg",
    colour: "bg-white",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/rainbowplacehospicewaikato/",
      },
    ],
  },
  {
    href: "https://www.orangatamariki.govt.nz/",
    name: "Oranga Tamariki",
    imageURL: "/logos/agencies/oranga-tamariki.png",
    colour: "bg-navy",
    social: [
      { type: "facebook", url: "https://www.facebook.com/OrangaTamariki" },
      {
        type: "youtube",
        url: "https://www.youtube.com/channel/UCWpNNGDSur1P7ytjZEfFfKA",
      },
    ],
  },
  {
    href: "https://www.camcomhouse.org.nz/",
    name: "The Cambridge Community House",
    imageURL: "/logos/agencies/cambridge-community-house.png",
    colour: "bg-white",
    social: [{ type: "facebook", url: "https://www.camcomhouse.org.nz/" }],
  },
  {
    href: "https://barnardos.org.nz/",
    name: "Barnados",
    imageURL: "/logos/agencies/barnados.svg",
    colour: "bg-white",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/BarnardosNZ/",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/barnardosnz/",
      },
    ],
  },
  {
    href: "https://www.anglicanaction.org.nz/",
    name: "Anglican Action",
    imageURL: "/logos/agencies/anglican-action.webp",
    colour: "bg-navy",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/anglicanaction",
      },
    ],
  },
  {
    href: "https://www.tekohaohealth.co.nz/",
    name: "Te Kohao Health",
    imageURL: "/logos/agencies/te-kohao-health.png",
    colour: "bg-white",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/tekohao",
      },
    ],
  },
  {
    href: "https://www.waikatowomensrefuge.co.nz/",
    name: "Waikato Women's Refuge",
    imageURL: "/logos/agencies/womens-refuge.png",
    colour: "bg-white",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/TeWhakaruruhau",
      },
    ],
  },
  {
    href: "https://www.tuuohomai.org.nz/",
    name: "Hamilton Abuse Intervention Project (HAIP)",
    imageURL: "/logos/agencies/tuu-oho-mai.png",
    colour: "bg-white",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/HAIPNZ/",
      },
    ],
  },
  {
    href: "https://www.tekura.school.nz/",
    name: "Te Kura - NZ Correspondance School",
    imageURL: "/logos/agencies/te-kura.png",
    colour: "bg-navy",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/tekuranz",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/tekuranz/",
      },
    ],
  },
  {
    href: "https://www.heartkids.org.nz/branches/waikato/",
    name: "Heart Kids",
    imageURL: "/logos/agencies/heart-kids.png",
    colour: "bg-red-500",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/heartkidsnz",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/heartkidsnz/",
      },
    ],
  },
  {
    href: "https://www.kokiritrust.org.nz/",
    name: "Kokiri Trust",
    imageURL: "/logos/agencies/kokiri-trust.png",
    colour: "bg-white",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/taumarunui.kokiritrust/",
      },
    ],
  },
  {
    href: "https://www.ka.org.nz/",
    name: "Kainga Aroha",
    imageURL: "/logos/agencies/kainga-aroha.png",
    colour: "bg-white",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/kaingaaroha",
      },
    ],
  },
  {
    href: "https://www.truecolours.org.nz/",
    name: "True Colours",
    imageURL: "/logos/agencies/true-colours.png",
    colour: "bg-white",
    social: [
      {
        type: "facebook",
        url: "https://facebook.com/truecoloursnz/",
      },
    ],
  },
  {
    href: "https://www.kiapuawai.nz/",
    name: "Kia Puawai",
    imageURL: "/logos/agencies/kiapuawai.svg",
    colour: "bg-navy",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/kiapuawai.nz/",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/kiapuawai.nz/",
      },
    ],
  },
  {
    href: "https://www.woodstock.school.nz/",
    name: "Woodstock School",
    imageURL: "/logos/agencies/woodstock-school.svg",
    colour: "bg-white",
    social: [
      {
        type: "facebook",
        url:
          "https://www.facebook.com/Woodstock-Primary-School-508294619256589",
      },
    ],
  },
  {
    href: "https://www.salvationarmy.org.nz/",
    name: "The Salvation Army",
    imageURL: "/logos/agencies/salvation-army.png",
    colour: "bg-white",
    social: [
      {
        type: "facebook",
        url: "https://www.facebook.com/SalvationArmyNZFTS",
      },
      {
        type: "instagram",
        url: "https://www.instagram.com/salvationarmynzfts/",
      },
    ],
  },
];

/* This example requires Tailwind CSS v3.0+ */
const OurValues = () => {
  return (
    <div className="relative mx-4 sm:mx-6 lg:mx-8 mt-4 md:mt-12 rounded-xl bg-white/25 overflow-hidden">
      <div className="relative h-80 overflow-hidden md:absolute md:left-0 md:h-full md:w-1/3 lg:w-1/2">
        <Image
          src="/images/recipients-hero.jpg"
          layout="fill"
          className="w-full object-cover"
          alt="Celebrate"
        />
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 926 676"
          aria-hidden="true"
          className="absolute left-24 -bottom-24 w-[57.875rem] transform-gpu blur-[118px]"
        >
          <path
            fill="url(#60c3c621-93e0-4a09-a0e6-4c228a0116d8)"
            fillOpacity=".4"
            d="m254.325 516.708-90.89 158.331L0 436.427l254.325 80.281 163.691-285.15c1.048 131.759 36.144 345.144 168.149 144.613C751.171 125.508 707.17-93.823 826.603 41.15c95.546 107.978 104.766 294.048 97.432 373.585L685.481 297.694l16.974 360.474-448.13-141.46Z"
          />
          <defs>
            <linearGradient
              id="60c3c621-93e0-4a09-a0e6-4c228a0116d8"
              x1="926.392"
              x2="-109.635"
              y1=".176"
              y2="321.024"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#776FFF" />
              <stop offset={1} stopColor="#FF4694" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div className="relative mx-auto max-w-7xl py-24 sm:py-32 lg:py-40 lg:px-8 ">
        <div className="pr-6 pl-6 md:ml-auto md:w-2/3 md:pl-16 lg:w-1/2 lg:pl-24 lg:pr-0 xl:pl-32">
          <Heading2>Our Vision</Heading2>
          <Heading3>Every child&apos;s birthday is celebrated</Heading3>
          <div className="mt-6">
            <Heading2>Our Mission</Heading2>
            <p className=" text-base leading-7 text-brand-navy">
              To ensure every child feels uniquely celebrated on their birthday
              regardless of circumstance. We aim to do that with extraordinary
              birthday cakes.
            </p>
          </div>
          <div className="mt-6">
            <Heading2>Our Message</Heading2>
            <p className=" text-base leading-7 text-brand-navy">
              Recipients have often experienced neglect, abuse, serious illness
              or family violence. What these cakes do is send a message of hope,
              love and solidarity:
            </p>
            <p className="text-center font-bold text-brand-navy m-8 text-xl">
              We see you. We care about you.
              <br />
              <br />
              You matter.
              <br />
              <br />
              You deserve love and happiness.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Recipients: NextPage = () => {
  return (
    <Page title="Recipients - The Cake Detective">
      <Page.Header />
      <Page.Content>
        <OurValues />
        <div className="bg-white/25 mx-4 sm:mx-6 lg:mx-8 my-4 md:my-12 rounded-xl">
          <div className="mx-auto max-w-7xl py-4 md:py-12 px-6 lg:px-8">
            <div className="space-y-12">
              <div className="text-center">
                <Heading2>Who we work with</Heading2>
                <Heading3>Agencies and organisations that help us out</Heading3>
                <p className="mt-4 text-xl text-brand-navy">
                  We work with some wonderful organisations and agencies in the
                  Waikato to find those who need our cakes
                </p>
              </div>
              <ul
                role="list"
                className="space-y-12 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:gap-y-12 sm:space-y-0 lg:grid-cols-3 lg:gap-x-8"
              >
                {agencies.map((agency) => (
                  <li key={agency.name}>
                    <div className="space-y-4">
                      <a
                        href={agency.href}
                        className={`p-4 ${agency.colour} rounded-lg block`}
                      >
                        <div className={`aspect-w-3 aspect-h-2`}>
                          <Image
                            className="rounded-lg object-contain shadow-lg m-4"
                            src={agency.imageURL}
                            alt={agency.name}
                            layout="fill"
                          />
                        </div>
                      </a>

                      <div className="space-y-2 text-center hidden">
                        <div className="space-y-1 text-lg font-medium leading-6 underline text-brand-ranga hover:text-brand-pink">
                          <a href={agency.href}>{agency.name}</a>
                        </div>
                        <ul
                          role="list"
                          className="flex space-x-5 justify-center"
                        >
                          {agency.social?.map((social) => {
                            const Icon = icons[social.type];
                            return (
                              <li key={social.type}>
                                <a
                                  href={social.url}
                                  className="text-brand-navy/50 hover:text-brand-pink"
                                >
                                  <span className="sr-only">{social.type}</span>
                                  <Icon
                                    className="h-6 w-6"
                                    aria-hidden="true"
                                  />
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <Thanks />
      </Page.Content>
      <Page.Footer />
    </Page>
  );
};

export default Recipients;
